import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { api } from "../lib/config";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { ArrowLeft, Printer, Loader2, LayoutGrid, Ticket, Search, Trash2, X } from "lucide-react";

const APP_ORIGIN = typeof window !== "undefined" ? window.location.origin : "";
const nameCollator = new Intl.Collator("es", { sensitivity: "base", numeric: true });

export default function CheckInQRSheet() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState("cards");
  const [q, setQ] = useState("");
  const [selectedIds, setSelectedIds] = useState(new Set());

  const load = async () => {
    try {
      const { data } = await api.get("/admin/registrations/checkin-tokens");
      setRows([...data].sort((a, b) => nameCollator.compare(a.full_name || "", b.full_name || "")));
    } catch {
      toast.error("No se pudo cargar la lista");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!localStorage.getItem("admin_token")) { nav("/admin/login"); return; }
    load();
  }, [nav]);

  const filtered = useMemo(() => {
    if (!q.trim()) return rows;
    const s = q.toLowerCase();
    return rows.filter((r) =>
      (r.full_name || "").toLowerCase().includes(s) ||
      (r.camper_number_display || "").toLowerCase().includes(s) ||
      (r.phone || "").includes(s)
    );
  }, [q, rows]);

  const printRows = selectedIds.size > 0 ? filtered.filter((r) => selectedIds.has(r.id)) : filtered;

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const del = async (r) => {
    if (!window.confirm(`¿Eliminar a "${r.full_name}" (${r.camper_number_display})?`)) return;
    try {
      await api.delete(`/admin/registrations/${r.id}`);
      toast.success("Inscripción eliminada");
      setSelectedIds((prev) => { const n = new Set(prev); n.delete(r.id); return n; });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo eliminar");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-yellow-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white text-black overflow-x-hidden" data-testid="checkin-qr-sheet">
      <div className="print:hidden sticky top-0 z-10 bg-black text-white px-3 sm:px-4 py-3 flex flex-wrap items-center gap-2 shadow-md">
        <Button
          variant="ghost" size="sm"
          onClick={() => nav("/admin/logistica")}
          data-testid="qr-back-btn"
          className="text-white/80 hover:text-yellow-400 hover:bg-white/5 flex-shrink-0"
        >
          <ArrowLeft className="w-4 h-4 mr-1" /> Logística
        </Button>
        <div className="flex items-center gap-1 bg-white/5 rounded-lg p-1">
          <button onClick={() => setMode("cards")} data-testid="qr-mode-cards"
            className={`px-2 sm:px-3 py-1.5 rounded text-[10px] font-bold uppercase tracking-widest flex items-center gap-1 ${mode === "cards" ? "bg-yellow-400 text-black" : "text-white/70 hover:text-white"}`}>
            <LayoutGrid className="w-3 h-3" /> Tarjetas
          </button>
          <button onClick={() => setMode("wristband")} data-testid="qr-mode-wristband"
            className={`px-2 sm:px-3 py-1.5 rounded text-[10px] font-bold uppercase tracking-widest flex items-center gap-1 ${mode === "wristband" ? "bg-yellow-400 text-black" : "text-white/70 hover:text-white"}`}>
            <Ticket className="w-3 h-3" /> Pulsera
          </button>
        </div>
        <div className="flex-1 min-w-[140px] relative">
          <Search className="w-4 h-4 text-white/40 absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar nombre o #…" data-testid="qr-search"
            className="pl-8 bg-white/5 border-white/10 !text-white placeholder:text-white/40 h-8 text-sm" />
        </div>
        {selectedIds.size > 0 && (
          <button onClick={() => setSelectedIds(new Set())} data-testid="qr-clear-selection"
            className="text-yellow-400 hover:text-yellow-300 text-[10px] uppercase tracking-widest font-bold flex items-center gap-1">
            <X className="w-3 h-3" /> {selectedIds.size} selec.
          </button>
        )}
        <Button onClick={() => window.print()} data-testid="qr-print-btn"
          className="bg-yellow-400 hover:bg-yellow-500 text-black font-bold uppercase tracking-widest text-xs">
          <Printer className="w-4 h-4 mr-1" /> Imprimir
        </Button>
      </div>

      <div className="print:hidden bg-yellow-50 border-b border-yellow-200 px-4 py-2 text-center">
        <div className="text-[11px] text-gray-600">
          {selectedIds.size > 0
            ? <>Imprimirá solo <b>{selectedIds.size}</b> seleccionados.</>
            : <>Imprimirá <b>{filtered.length}</b> {filtered.length === 1 ? "resultado" : "resultados"}.</>}
          {mode === "wristband" && <span className="block sm:inline"> · Pulsera 3″×0.75″</span>}
          <span data-testid="qr-alphabetical-order-label"> · Orden A–Z</span>
        </div>
      </div>

      {mode === "cards"
        ? <CardsGrid allRows={filtered} printRows={printRows} selectedIds={selectedIds} onToggle={toggleSelect} onDelete={del} />
        : <WristbandGrid allRows={filtered} printRows={printRows} selectedIds={selectedIds} onToggle={toggleSelect} onDelete={del} />}

      {filtered.length === 0 && <div className="text-center py-16 text-gray-400 text-sm">Sin resultados</div>}

      <style>{`
        @media print {
          @page { size: letter; margin: 0.4in; }
          body { background: white !important; }
          .print\\:hidden { display: none !important; }
          .wristband-grid { gap: 0 !important; }
        }
      `}</style>
    </div>
  );
}

function CardsGrid({ allRows, printRows, selectedIds, onToggle, onDelete }) {
  const printSet = new Set(printRows.map((r) => r.id));
  return (
    <div className="p-3 sm:p-6 grid grid-cols-2 sm:grid-cols-3 gap-3 print:grid-cols-3 print:gap-2">
      {allRows.map((r) => {
        const url = `${APP_ORIGIN}/checkin/${r.id}?t=${r.token}`;
        const willPrint = printSet.has(r.id);
        const isSel = selectedIds.has(r.id);
        return (
          <div key={r.id} data-testid={`qr-card-${r.id}`}
            className={`relative border-2 rounded-lg p-3 flex flex-col items-center bg-white break-inside-avoid ${willPrint ? "border-black" : "border-gray-200 opacity-40 print:hidden"}`}
            style={{ pageBreakInside: "avoid" }}>
            <div className="print:hidden absolute top-1 right-1 flex gap-1">
              <button onClick={() => onToggle(r.id)} data-testid={`qr-select-${r.id}`}
                className={`w-5 h-5 rounded border-2 flex items-center justify-center text-[10px] font-bold ${isSel ? "bg-yellow-400 border-yellow-500 text-black" : "border-gray-300 bg-white hover:border-gray-400"}`}
                title="Seleccionar">{isSel && "✓"}</button>
              <button onClick={() => onDelete(r)} data-testid={`qr-delete-${r.id}`}
                className="w-5 h-5 rounded border border-red-300 bg-white text-red-500 hover:bg-red-500 hover:text-white flex items-center justify-center" title="Eliminar">
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
            <div className="w-full flex items-center justify-between mb-2">
              <span className="w-3 h-3 rounded-full border border-black" style={{ background: r.color || "#ffffff" }} />
              <span className="font-mono text-xs font-bold text-yellow-700">{r.camper_number_display}</span>
            </div>
            <QRCodeSVG value={url} size={128} level="M" includeMargin={false} />
            <div className="mt-2 w-full text-center">
              <div className="font-bold text-sm leading-tight line-clamp-2">{r.full_name}</div>
              <div className="text-[9px] text-gray-500 mt-0.5">Escanea para check-in</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function WristbandGrid({ allRows, printRows, selectedIds, onToggle, onDelete }) {
  const printSet = new Set(printRows.map((r) => r.id));
  return (
    <div className="wristband-grid p-3 sm:p-6 grid gap-2 print:gap-0 print:p-0"
      style={{ gridTemplateColumns: "repeat(auto-fit, minmax(min(3in, 100%), max-content))", justifyContent: "center" }}>
      {allRows.map((r) => {
        const url = `${APP_ORIGIN}/checkin/${r.id}?t=${r.token}`;
        const willPrint = printSet.has(r.id);
        const isSel = selectedIds.has(r.id);
        return (
          <div key={r.id} data-testid={`qr-wristband-${r.id}`}
            className={`relative bg-white flex items-center break-inside-avoid overflow-hidden ${willPrint ? "" : "opacity-40 print:hidden"}`}
            style={{ width: "3in", height: "0.75in", border: "1px dashed #999", padding: "0.05in", pageBreakInside: "avoid", boxSizing: "border-box" }}>
            <div className="print:hidden absolute top-0 right-0 flex gap-0.5 bg-white/90 rounded-bl">
              <button onClick={() => onToggle(r.id)} data-testid={`qr-select-${r.id}`}
                className={`w-4 h-4 border text-[9px] font-bold flex items-center justify-center ${isSel ? "bg-yellow-400 border-yellow-500 text-black" : "border-gray-300 bg-white"}`} title="Seleccionar">
                {isSel && "✓"}
              </button>
              <button onClick={() => onDelete(r)} data-testid={`qr-delete-${r.id}`}
                className="w-4 h-4 border border-red-300 text-red-500 hover:bg-red-500 hover:text-white flex items-center justify-center" title="Eliminar">
                <Trash2 className="w-2.5 h-2.5" />
              </button>
            </div>
            <div className="flex-shrink-0" style={{ width: "0.65in", height: "0.65in" }}>
              <QRCodeSVG value={url} size={62} level="M" includeMargin={false} />
            </div>
            <div className="flex-1 min-w-0 ml-1 flex flex-col justify-between h-full py-0.5">
              <div className="flex items-center gap-1">
                <span className="rounded-full border border-black flex-shrink-0" style={{ width: "0.08in", height: "0.08in", background: r.color || "#ffffff" }} />
                <span className="font-bold leading-tight truncate" style={{ fontSize: "8pt", fontFamily: "sans-serif" }} title={r.full_name}>
                  {r.full_name}
                </span>
              </div>
              <div className="flex items-center justify-between leading-none">
                <span style={{ fontSize: "9pt", fontWeight: 700, fontFamily: "monospace", color: "#a16207" }}>{r.camper_number_display}</span>
                <span style={{ fontSize: "5.5pt", letterSpacing: "0.06em", color: "#666", textTransform: "uppercase", fontWeight: 700 }}>Op. Rescate</span>
              </div>
              <div style={{ fontSize: "5pt", color: "#999", letterSpacing: "0.04em", textTransform: "uppercase" }}>Escanea → check-in</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
